import axios from 'axios'
import {AuthModel, UserModel} from './_models'
import {getUser, getAuth} from './AuthHelpers'
import {setLanguage} from '../../../../_metronic/i18n/Metronici18n'

const API_URL = process.env.REACT_APP_API_URL
const API_URL_PROVIDER = process.env.REACT_APP_API_URL_PROVIDER_AUTH
const API_URL_PROVIDER_AUTH = process.env.REACT_APP_API_URL_PROVIDER
const LOCAL_API_URL = `${process.env.REACT_APP_API_USER_URL}`

export const GET_USER_BY_ACCESSTOKEN_URL = `${API_URL}/verify_token`
export const LOGIN_URL = `${process.env.REACT_APP_API_USER_URL}/api/Users/Provider/login`
export const LOCAL_LOGIN_URL = `${process.env.REACT_APP_API_USER_URL}/api/Users/Provider/login`
export const REGISTER_URL = `${API_URL}/register`
export const REQUEST_PASSWORD_URL = `${API_URL}/forgot_password`

export async function login(email: string, password: string) {
  const defaultMenuAccess = {
    claim: {
      view: false,
      export: false,
      edit: false,
      create: false,
    },
    provider: {
      view: false,
      export: false,
      edit: false,
      create: false,
    },
    icd: {
      view: false,
      export: false,
      edit: false,
      create: false,
    },
    accountprovider: {
      view: false,
      export: false,
      edit: false,
      create: false,
    },
    activationprovider: {
      view: false,
      export: false,
      edit: false,
      create: false,
    },
  }

  // Store the first error to throw if all attempts fail
  let firstError: any = null

  try {
    // First, try the existing provider login
    const response = await axios.post<AuthModel>(LOGIN_URL, {
      email,
      password,
    })
    console.log('Provider Login response:', response.data)

    // Set language based on provider country
    if (response.data.provider?.providerCountry) {
      const language =
        response.data.provider.providerCountry?.toUpperCase() === 'INDONESIA' ? 'id' : 'en'
      setLanguage(language) // Don't redirect on login
    }

    // Parse menu access if it's a string
    let menuAccess = defaultMenuAccess
    if (response.data.menu) {
      menuAccess =
        typeof response.data.menu === 'string' ? JSON.parse(response.data.menu) : response.data.menu
    }

    const authWithEmail = {
      ...response.data,
      userEmail: email,
      token: response.data.token || response.data.data?.token || '',
      menu: {
        claim: {
          view: menuAccess.claim?.view ?? false,
          export: menuAccess.claim?.export ?? false,
          edit: menuAccess.claim?.edit ?? false,
          create: menuAccess.claim?.create ?? false,
        },
        provider: {
          view: menuAccess.provider?.view ?? false,
          export: menuAccess.provider?.export ?? false,
          edit: menuAccess.provider?.edit ?? false,
          create: menuAccess.provider?.create ?? false,
        },
        icd: {
          view: menuAccess.icd?.view ?? false,
          export: menuAccess.icd?.export ?? false,
          edit: menuAccess.icd?.edit ?? false,
          create: menuAccess.icd?.create ?? false,
        },
        accountprovider: {
          view: menuAccess.accountprovider?.view ?? false,
          export: menuAccess.accountprovider?.export ?? false,
          edit: menuAccess.accountprovider?.edit ?? false,
          create: menuAccess.accountprovider?.create ?? false,
        },
        activationprovider: {
          view: menuAccess.activationprovider?.view ?? false,
          export: menuAccess.activationprovider?.export ?? false,
          edit: menuAccess.activationprovider?.edit ?? false,
          create: menuAccess.activationprovider?.create ?? false,
        },
      },
    }
    console.log('Stored provider auth data:', authWithEmail)
    return {data: authWithEmail}
  } catch (error: any) {
    console.log('Provider login failed, trying local login...', error)
    firstError = error

    // Try the local login
    try {
      const localResponse = await axios.post<AuthModel>(LOCAL_LOGIN_URL, {
        email,
        password,
      })
      console.log('Local Login response:', localResponse.data)

      // Set language based on provider country
      if (localResponse.data.provider?.providerCountry) {
        const language =
          localResponse.data.provider.providerCountry?.toUpperCase() === 'INDONESIA' ? 'id' : 'en'
        setLanguage(language) // Don't redirect on login
      }

      // Parse menu access if it's a string
      let menuAccess = defaultMenuAccess
      if (localResponse.data.menu) {
        menuAccess =
          typeof localResponse.data.menu === 'string'
            ? JSON.parse(localResponse.data.menu)
            : localResponse.data.menu
      }

      // Transform the local response to match the expected format
      const transformedAuth = {
        isSuccess: localResponse.data.isSuccess,
        message: localResponse.data.message,
        token: localResponse.data.data?.token || '',
        data: {
          token: localResponse.data.data?.token || '',
          refreshToken: localResponse.data.data?.refreshToken,
          expiration: localResponse.data.data?.expiration,
        },
        provider: localResponse.data.provider,
        userEmail: email,
        menu: {
          claim: {
            view: menuAccess.claim?.view ?? false,
            export: menuAccess.claim?.export ?? false,
            edit: menuAccess.claim?.edit ?? false,
            create: menuAccess.claim?.create ?? false,
          },
          provider: {
            view: menuAccess.provider?.view ?? false,
            export: menuAccess.provider?.export ?? false,
            edit: menuAccess.provider?.edit ?? false,
            create: menuAccess.provider?.create ?? false,
          },
          icd: {
            view: menuAccess.icd?.view ?? false,
            export: menuAccess.icd?.export ?? false,
            edit: menuAccess.icd?.edit ?? false,
            create: menuAccess.icd?.create ?? false,
          },
          accountprovider: {
            view: menuAccess.accountprovider?.view ?? false,
            export: menuAccess.accountprovider?.export ?? false,
            edit: menuAccess.accountprovider?.edit ?? false,
            create: menuAccess.accountprovider?.create ?? false,
          },
          activationprovider: {
            view: menuAccess.activationprovider?.view ?? false,
            export: menuAccess.activationprovider?.export ?? false,
            edit: menuAccess.activationprovider?.edit ?? false,
            create: menuAccess.activationprovider?.create ?? false,
          },
        },
      }

      console.log('Stored local auth data:', transformedAuth)
      return {data: transformedAuth}
    } catch (localError: any) {
      // Log both errors for debugging
      console.error('Provider login error:', firstError)
      console.error('Local login error:', localError)

      // Throw a combined error message
      // throw new Error(
      //   `Authentication failed. Provider error: ${
      //     firstError?.response?.data?.message || firstError?.message
      //   }. ` + `Local error: ${localError?.response?.data?.message || localError?.message}`
      // )
      throw new Error(
        `Authentication failed. Provider error: ${
          firstError?.response?.data?.message || firstError?.message
        }`
      )
    }
  }
}

export function register(
  email: string,
  firstname: string,
  lastname: string,
  password: string,
  password_confirmation: string
) {
  return axios.post(REGISTER_URL, {
    email,
    first_name: firstname,
    last_name: lastname,
    password,
    password_confirmation,
  })
}

export function activationPassword(token: string, email: string, password: string) {
  return axios.post<{result: boolean}>(
    `${process.env.REACT_APP_API_USER_URL}/api/UserActivation/activate`,
    {
      email,
      token,
      password,
      source: 'provider',
    }
  )
}
export function requestPassword(email: string) {
  return axios.post<{result: boolean}>(REQUEST_PASSWORD_URL, {
    email,
  })
}

export function getUserByToken(token: string): Promise<UserModel> {
  return new Promise((resolve, reject) => {
    try {
      const auth = getAuth()
      const decodedToken = decodeToken(token)

      if (decodedToken && auth) {
        const user: UserModel = {
          id: 0,
          username: auth.userEmail || decodedToken.sub, // Use stored email if available
          password: '',
          first_name: '',
          last_name: '',
          email: auth.userEmail || decodedToken.sub, // Use stored email if available
          fullname: auth.provider?.providerName || decodedToken.sub,
          roles: [],
          providerCode: auth.provider?.providerCode,
          providerName: auth.provider?.providerName,
          providerAddress: auth.provider?.providerAddress,
          providerCity: auth.provider?.providerCity,
          providerProvince: auth.provider?.providerProvince,
          providerCountry: auth.provider?.providerCountry,
          isActive: auth.provider?.isActive || false,
          roleId: auth.provider?.roleId,
          menuAccess: {
            claim: auth.menu?.claim || {
              view: false,
              export: false,
              edit: false,
              create: false,
            },
            provider: auth.menu?.provider || {
              view: false,
              export: false,
              edit: false,
              create: false,
            },
            icd: auth.menu?.icd || {
              view: false,
              export: false,
              edit: false,
              create: false,
            },
            accountprovider: auth.menu?.accountprovider || {
              view: false,
              export: false,
              edit: false,
              create: false,
            },
            activationprovider: auth.menu?.activationprovider || {
              view: false,
              export: false,
              edit: false,
              create: false,
            },
          },
        }
        resolve(user)
      } else {
        reject(new Error('Invalid token or missing auth data'))
      }
    } catch (error) {
      reject(error)
    }
  })
}

function decodeToken(token: string) {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error('Error decoding token:', error)
    return null
  }
}
